<template>
    <Card :title="$t('pages.AiProcessCard')">
            <div class="card-body" v-if="aiProcess">
                <Alert type="warning" margin-bottom="mb-4" :dismissible="false" v-if="aiProcess.batch?.cancelled" class="d-flex align-items-center justify-content-between">
                    <span>
                        <strong>{{ $t('alerts.ai_process_cancelled_title') }}</strong>
                        {{ $t('alerts.ai_process_cancelled_before_link') }}
                        <router-link :to="{ name: 'IndexTariffsPage' }">{{ $t('alerts.ai_process_cancelled_link') }}</router-link>
                        {{ $t('alerts.ai_process_cancelled_after_link') }}
                    </span>
                    <AppButton color="warning" class="ml-3" @click="continueProcess"><i class="icon-play3 mr-2"></i>{{ $t('buttons.continue') }}</AppButton>
                </Alert>
                <div class="row">
                    <div class="col-lg-4">
                        <FormGroup :label="$t('labels.ai_process_generate_fields')">
                            <Input :model-value="generateFieldsLabel" disabled />
                        </FormGroup>
                    </div>
                    <div class="col-lg-4">
                        <FormGroup :label="$t('labels.ai_process_total_jobs')">
                            <Input :model-value="String(totalProductsCount)" disabled />
                        </FormGroup>
                    </div>
                    <div class="col-lg-4">
                        <FormGroup :label="$t('labels.ai_process_processed_jobs')">
                            <Input :model-value="String(doneProductsCount)" disabled />
                        </FormGroup>
                    </div>
                    <div class="col-12" v-if="aiProcess.settings?.user_comment">
                        <FormGroup :label="$t('labels.ai_process_user_comment')">
                            <textarea class="form-control" :value="aiProcess.settings.user_comment" disabled rows="3" />
                        </FormGroup>
                    </div>
                </div>
            </div>
            <div class="card-footer d-flex align-items-center justify-content-between" v-if="aiProcess">
                <div class="d-flex align-items-center">
                    <AppCheckbox :model-value="applyToOtherIntegrations" @update:model-value="onApplyToOtherIntegrationsChange" />
                    <span class="ml-2">{{ $t('labels.ai_process_apply_to_other_integrations') }}</span>
                </div>
                <div class="d-flex">
                    <AppButton color="light" class="mr-2" @click="cancel"><i class="icon-trash mr-2"></i>{{ $t('buttons.cancel') }}</AppButton>
                    <AppButton color="indigo" :disabled="!isConfirmable" @click="confirm"><i class="icon-floppy-disk mr-2"></i>{{ $t('labels.ai_process_apply_btn') }}</AppButton>
                </div>
            </div>
        </Card>

        <Card :title="$t('pages.AiProcessTasksCard')" class="mt-3">
            <NavTab class="nav-tabs-bottom mb-0" has-wrapper>
                <NavTabItem
                    v-for="tab in statusTabs"
                    :key="tab.status"
                    :href="`#ai-process-products-${tab.status}`"
                    :ref="setTabRef"
                    @click="selectStatus(tab.status)"
><AiProcessTaskStatusComponent :status="tab.status" :count="aiProcess?.products_count_by_status?.[tab.status] ?? 0" :spinner="false" /></NavTabItem>
                <template #content>
                    <NavTabPane
                        v-for="tab in statusTabs"
                        :key="tab.status"
                        :id="`ai-process-products-${tab.status}`"
                    >
                        <FetchTable
                            :url="`/projects/${$route.params.projectUuid}/pim/integrations/${integration.uuid}/ai-processes/${$route.params.aiProcessUuid}/tasks`"
                            :handle="tableHandles[tab.status]"
                            :columns="columns"
                            :orders="orders"
                            has-checkbox
                            @update:orders="orders = $event"
                            @update:selectedValues="selectedValues = $event"
                        >
                            <template v-if="['success', 'confirmed', 'cancelled'].includes(tab.status)" #actionDropdownItems>
                                <DropdownItem @click="updateSelectedStatus('confirmed')"><i class="icon-checkmark-circle mr-2"></i>{{ $t('buttons.confirm') }}</DropdownItem>
                                <DropdownItem @click="updateSelectedStatus('cancelled')"><i class="icon-blocked mr-2"></i>{{ $t('buttons.cancel') }}</DropdownItem>
                            </template>
                            <template #td-id="{row}">
                                <router-link :to="{ name: 'AiProcessTaskEditPage', params: { aiProcessUuid: $route.params.aiProcessUuid, aiProcessTaskUuid: row.uuid } }">
                                    {{ row.id }}
                                </router-link>
                            </template>
                            <template #td-product="{row}">
                                <div class="d-flex align-items-center">
                                    <ImageZoomComponent
                                        :url="row.product.picture_url ?? PLACEHOLDER_IMAGE_URL"
                                        :alt="row.product.name"
                                    />
                                    <div class="d-flex flex-column ml-2">
                                        <span class="long-string">{{ row.product.name }}</span>
                                        <span class="text-muted">{{ row.product.sku }}</span>
                                    </div>
                                </div>
                            </template>
                            <template #td-status="{row}">
                                <AiProcessTaskStatusComponent :status="row.status" :exception-reason="row.exception_reason" />
                            </template>
                            <template #td-created_at="{row}">
                                {{ DateFormatter.formatDateTime(row.created_at) }}
                            </template>
                            <template #td-updated_at="{row}">
                                {{ DateFormatter.formatDateTime(row.updated_at) }}
                            </template>
                        </FetchTable>
                    </NavTabPane>
                </template>
            </NavTab>
        </Card>
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import AppButton from "../../../../components/new/button/AppButton.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import MessageNotificator from "../../../../services/MessageNotificator";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import FetchTable from "../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../components/new/table/tableHandle";
import NavTab from "../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../components/tabs/NavTabItem.vue";
import NavTabPane from "../../../components/tabs/NavTabPane.vue";
import DropdownItem from "../../../components/btnDropdown/DropdownItem.vue";
import DateFormatter from "../../../../services/DateFormatter";
import { httpClient } from "../../../../services/HttpClient";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants";
import ImageZoomComponent from "../../../../components/new/image/ImageZoomComponent.vue";
import { mapGetters } from "vuex";
import AiProcessTaskStatusComponent from "../../../components/modules/integration/AiProcessTaskStatusComponent.vue";
import Alert from "../../../../components/AlertComponent.vue";
import { getAiProcessFieldLabelKey, getAiProcessFields } from "../../../enums/AiProcessFieldEnum";

const AI_PROCESS_STATUSES = ['waiting', 'process', 'success', 'problem', 'confirmed', 'cancelled'];

export default {
    name: 'AiProcessEditPage',
    components: { Card, FormGroup, Input, FetchTable, NavTab, NavTabItem, NavTabPane, DropdownItem, ImageZoomComponent, AiProcessTaskStatusComponent, AppCheckbox, AppButton, Alert },
    props: {
        integration: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            aiProcess: null,
            applyToOtherIntegrations: false,
            activeStatus: 'waiting',
            selectedValues: [],
            tabRefs: [],
            tableHandles: Object.fromEntries(
                AI_PROCESS_STATUSES.map((status) => [status, createTableHandle()]),
            ),
            DateFormatter,
            PLACEHOLDER_IMAGE_URL,
            orders: [
                { column: 'id', dir: 'asc' },
            ],
            columns: [
                { key: 'id',         name: this.$t('table.titles.id'),         width: '100px', type: 'string', order: true },
                { key: 'product',    name: this.$t('table.titles.name'),        width: 'auto',  type: 'string', order: false },
                { key: 'status',     name: this.$t('table.titles.status'),      width: '180px', type: 'string', order: true },
                { key: 'created_at', name: this.$t('table.titles.created_at'),  width: '200px', type: 'date',   order: true },
                { key: 'updated_at', name: this.$t('table.titles.updated_at'),  width: '200px', type: 'date',   order: true },
            ],
        };
    },
    computed: {
        ...mapGetters({
            project: 'route/project',
            profile: 'profile/profile',
        }),
        totalProductsCount() {
            const counts = this.aiProcess?.products_count_by_status;
            if (!counts) return 0;
            return Object.values(counts).reduce((sum, n) => sum + n, 0);
        },
        doneProductsCount() {
            const counts = this.aiProcess?.products_count_by_status;
            if (!counts) return 0;
            return Object.entries(counts)
                .filter(([s]) => s !== 'waiting' && s !== 'process')
                .reduce((sum, [, n]) => sum + n, 0);
        },
        isConfirmable() {
            const counts = this.aiProcess?.products_count_by_status;
            if (!counts) return false;
            return counts.process === 0 && counts.confirmed > 0;
        },
        statusTabs() {
            return AI_PROCESS_STATUSES.map(status => ({
                status,
                label: this.$t(`labels.ai_process_statuses.${status}`),
            }));
        },
        generateFieldsLabel() {
            const labels = getAiProcessFields(this.aiProcess?.settings)
                .map(field => this.$t(getAiProcessFieldLabelKey(field)));

            return labels.join(', ') || '—';
        },
    },
    async mounted() {
        await this.fetchProcess();

        this.$nextTick(() => {
            this.tabRefs[0].click();
        });

        this.emitter.on('notification:received', this.onNotification);
    },
    beforeUnmount() {
        this.emitter.off('notification:received', this.onNotification);
    },
    beforeUpdate() {
        this.tabRefs = [];
    },
    methods: {
        onNotification(notification) {
            if (notification.type !== 'App\\Notifications\\Pim\\AiProcessTask\\AiProcessTaskStatusChangedNotification') {
                return;
            }

            const { ai_process_uuid, product_uuid, status, processed_jobs, product_updated_at, products_count_by_status } = notification.additional_data;

            if (ai_process_uuid !== this.$route.params.aiProcessUuid) {
                return;
            }

            const row = this.tableHandles[this.activeStatus].rows.find(r => r.product.uuid === product_uuid);

            if (row) {
                row.status = status;
                row.updated_at = product_updated_at;
            }

            if (this.aiProcess?.batch) {
                this.aiProcess.batch.processed_jobs = processed_jobs;
                this.aiProcess.batch.pending_jobs = this.aiProcess.batch.total_jobs - processed_jobs;
            }

            if (products_count_by_status) {
                this.aiProcess.products_count_by_status = products_count_by_status;
            }
        },
        setTabRef(el) {
            if (el) {
                this.tabRefs.push(el);
            }
        },
        selectStatus(status) {
            this.activeStatus = status;
            this.selectedValues = [];
            this.tableHandles[status].fetchRows({ statuses: [status] });
        },
        async fetchProcess() {
            const response = await httpClient.get(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}`
            );
            this.aiProcess = response.data.data;
            this.applyToOtherIntegrations = this.aiProcess.apply_to_other_integrations;
        },
        async updateSelectedStatus(status) {
            await Promise.all(
                this.selectedValues.map(uuid => httpClient.patch(
                    `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/tasks/${uuid}`,
                    { status }
                ))
            );

            new MessageNotificator().success(this.$t('alerts.success'));
            await this.fetchProcess();
            this.tableHandles[this.activeStatus].fetchRows({ statuses: [this.activeStatus] });
        },
        async confirm() {
            await httpClient.post(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/confirm`
            );

            new MessageNotificator().success(this.$t('alerts.success'));

            this.$router.push({ name: 'AiProcessesListPage' });
        },
        async cancel() {
            await httpClient.post(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/cancel`
            );

            new MessageNotificator().success(this.$t('alerts.success'));

            this.$router.push({ name: 'AiProcessesListPage' });
        },
        async continueProcess() {
            await httpClient.post(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}/continue`
            );

            new MessageNotificator().success(this.$t('alerts.success'));
            await this.fetchProcess();
        },
        onApplyToOtherIntegrationsChange(value) {
            this.applyToOtherIntegrations = value;
            httpClient.put(
                `/projects/${this.$route.params.projectUuid}/pim/integrations/${this.integration.uuid}/ai-processes/${this.$route.params.aiProcessUuid}`,
                { apply_to_other_integrations: value }
            ).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'));
            });
        },
    },
};
</script>
